import { chunk, isEqual } from 'lodash';
import { useEffect, useMemo, useState } from 'react';

import useAudioRoutingStore from 'state/audioRoutingStore';
import useVoTextAnimation from 'state/voTextAnimation';

const isEndOfSentence = (word: string) => {
  const w = word?.trim();
  const symbols = ['.', '!', '?'];
  return symbols.some(s => w?.endsWith(s));
};

const isStartOfSentence = (word: string) => /^[A-Z]/.test(word?.trim());

const reshapeArray = (target: any[], source: any[][]) => {
  const result = [];
  let index = 0;

  for (const subArray of source) {
    const count = subArray.length;
    result.push(target.slice(index, index + count));
    index += count;
  }

  return result;
};

const mergeChunks = (chunks: string[][], minimum: number = 3): string[][] => {
  // If last chunk is too short
  if (chunks.length > 1 && chunks[chunks.length - 1].length <= minimum) {
    const lastChunk = chunks.pop();
    chunks[chunks.length - 1] = chunks[chunks.length - 1].concat(
      lastChunk ?? []
    );
  }

  // Check for bad start/end of chunk
  for (let i = 0; i < chunks.length - 1; i++) {
    const currentChunk = chunks[i];
    const nextChunk = chunks[i + 1];
    if (!nextChunk || !currentChunk) {
      continue;
    }
    // If last two words end a sentence and start a new one - 'word. Word'
    if (
      currentChunk.length > 1 &&
      isEndOfSentence(currentChunk[currentChunk.length - 2]) &&
      isStartOfSentence(currentChunk[currentChunk.length - 1])
    ) {
      const lastWord = currentChunk.pop();
      lastWord && nextChunk.unshift(lastWord);
    }
    // If the first word of the next chunk ends with a period and is not uppercase
    if (
      nextChunk.length > 0 &&
      isEndOfSentence(nextChunk[0]) &&
      !isStartOfSentence(nextChunk[0])
    ) {
      const firstWord = nextChunk.shift();
      firstWord && currentChunk.push(firstWord);
    }
  }

  return chunks;
};

const useKaraokeText = (wordsLimit = 8) => {
  const { time } = useVoTextAnimation();
  const { audioChunks, currentStep, isPlaying } = useAudioRoutingStore();

  const [currentChunkIndex, setCurrentChunkIndex] = useState(0);
  const [wordsChunks, setWordsChunks] = useState<string[][]>([]);
  const [timesChunks, setTimesChunks] = useState<number[][]>([]);
  const [wordsSaid, setWordsSaid] = useState<string[]>([]);

  const currentVoiceOver = useMemo(
    () => audioChunks?.[currentStep] || null,
    [currentStep, audioChunks]
  );

  useEffect(() => {
    // calculate words chunks and times chunks
    const words = currentVoiceOver?.alignment.words || [];
    const startTimes = currentVoiceOver?.alignment.word_start_times || [];

    const wChunks = mergeChunks(chunk(words, wordsLimit));
    const tChunks: number[][] = reshapeArray(startTimes, wChunks);

    setTimesChunks(tChunks);
    setWordsChunks(wChunks);
  }, [
    currentVoiceOver?.alignment.word_start_times,
    currentVoiceOver?.alignment.words,
    wordsLimit,
  ]);

  useEffect(() => {
    if (!timesChunks[currentChunkIndex] || !wordsChunks[currentChunkIndex]) {
      return;
    }

    const filtered =
      timesChunks[currentChunkIndex]
        .filter(wordStartTime => wordStartTime <= time)
        .map((_, index) => wordsChunks[currentChunkIndex][index]) || [];

    setWordsSaid(filtered);
  }, [currentChunkIndex, time, timesChunks, wordsChunks]);

  useEffect(() => {
    if (!isPlaying) return;
    if (!isEqual(wordsChunks?.[currentChunkIndex] || [], wordsSaid)) return;
    if (currentChunkIndex === wordsChunks.length) return;

    setCurrentChunkIndex(currentChunkIndex + 1);
  }, [wordsChunks, wordsSaid, currentChunkIndex, isPlaying]);

  useEffect(() => {
    setCurrentChunkIndex(0);
  }, [currentStep]);

  return { wordsSaid, wordsChunk: wordsChunks[currentChunkIndex] };
};

export default useKaraokeText;
